package com.neurobs.react

import slinky.core.FunctionalComponent
import slinky.core.annotations.react
import slinky.core.facade.Hooks.useState
import slinky.web.html._

case class RadioProperties
(
	captions: Seq[String],
	selectIndex: Int
)

/**
 * Helper class that generated radio controls given a list of objects of type T
 */
case class RadioState[T] private (items: Seq[T], selectIndex: Int, toCaption: T => String) {
	def generateProperties(): RadioProperties = {
		RadioProperties(captions = items.map(toCaption), selectIndex)
	}

	def value: T = items(selectIndex)

	def newSelection(newSelectIndex: Int): RadioState[T] = {
		if ((newSelectIndex < 0) || (newSelectIndex >= items.size)) throw new RuntimeException(s"Invalid selectIndex $newSelectIndex for radio control with ${items.size} items.")
		copy(selectIndex = newSelectIndex)
	}
}

object RadioState {
	def apply[T](items: Seq[T], value: T, toCaption: T => String): RadioState[T] = {
		val selectIndex = items.indexOf(value)
		if (selectIndex < 0) throw new RuntimeException(s"Invalid value $value for radio control.")
		RadioState[T](items = items, selectIndex = selectIndex, toCaption = toCaption)
	}

	def booleanRadio(value: Boolean, trueCaption: String, falseCaption: String): RadioState[Boolean] = {
		RadioState[Boolean](Seq(false, true), value, (v: Boolean) => if (v) trueCaption else falseCaption)
	}
}

/**
 * A collection of "controlled" radio buttons arranged horizontally.
 */
@react object Radio {
	case class Props
	(
		props: RadioProperties,
		onSelectionChange: Int => Unit
	)

	val component = FunctionalComponent[Props] { props =>
		val nameState = useState(generateName())
		span(
			props.props.captions.zipWithIndex.map { e =>
				span(key := e._2.toString)(
					input(
						`type` := "radio",
						className := "efactory-radio",
						name := nameState._1,
						onChange := {() => props.onSelectionChange(e._2)},
						checked := e._2 == props.props.selectIndex
					),
					span(style := StyleUtil.makeStyle(("marginLeft", "0.3em"), ("marginRight", "1em")))(e._1)
				)
			}
		)
	}

	private var nameCounter = 0
	private def generateName(): String = {
		nameCounter += 1
		"RadioGroup" + nameCounter.toString
	}
}
